import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import { toUtcLongDate, isLate } from '../../../../../../utils/date-helpers';
import { findAssigneeInOxygenMappingById } from '../../../../../../utils/userHelpers';
import {
  DocumentCell,
  AssigneeCell,
  StatusColorIndicator,
  Text,
  MatrixTable,
  IconCell,
  ConnectedLinkedEntitiesCell,
  ENTITY_TYPES,
} from '@adsk/bim360-matrix-react-components';
import pco from '@adsk/bim360-matrix-react-components/es/plugins/pco';
import meeting from '@adsk/bim360-matrix-react-components/es/plugins/meeting-item';
import issue from '@adsk/bim360-matrix-react-components/es/plugins/issue';
import { statusColorMapping } from '../../../../../../constants/status-types';
import { TooltipPositionings } from '../../../../../../constants/common';
import { getDocumentName, isDocumentUnavailable } from '../../../../../../redux/selectors';
import {
  sortPropType,
  issueWrapperArrayType,
  documentCollectionType,
  userType,
  oxygenType,
} from '../../../../../../constants/proptype-validation/issue-proptypes';
import featureFlags, { FEATURE_FLAGS } from '../../../../../../utils/featureFlags';
import StatusCell from './components/StatusCell';
import { isSingleUserAssignee } from '../../../../../../utils/assigned-to-helpers';
import { showFieldColumnInTable, hideFieldColumnInTable } from "../../../../../../utils/analytics-helpers";

const Alignment = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
};

// Defining column names for table use.
const ISSUE_COLUMN_TYPES = {
  STATUS: 'STATUS',
  ID: 'ID',
  TITLE: 'TITLE',
  LOCATION: 'LOCATION',
  CREATED_BY: 'CREATED_BY',
  ASSIGNED_TO: 'ASSIGNED_TO',
  COMPANY: 'COMPANY',
  DUE_DATE: 'DUE_DATE',
  DOCUMENT: 'DOCUMENT',
  ATTACHMENTS: 'ATTACHMENTS',
  COMMENTS: 'COMMENTS',
  COST: 'COST',
  SCHEDULE: 'SCHEDULE',
  PRIORITY: 'PRIORITY',
  DISCIPLINE: 'DISCIPLINE',
  CATEGORY: 'CATEGORY',
  REFERENCE: 'REFERENCE',
  LINKED_REFERENCES: 'LINKED_REFERENCES',
};

// Defining sortable column names
const SORTABLE_COLUMN_NAMES = {
  STATUS: 'status',
  ID: 'custom_identifier',
  TITLE: 'title',
  LOCATION: 'location_description',
  ASSIGNED_TO: 'assigned_to',
  COMPANY: 'company',
  DUE_DATE: 'due_date',
  DOCUMENT: 'document',
  ATTACHMENTS: 'attachments',
  COMMENTS: 'comments',
  COST: 'cost_impact',
  SCHEDULE: 'schedule_impact',
  PRIORITY: 'priority',
  CATEGORY: 'category',
  DISCIPLINE: 'discipline',
  REFERENCE: 'reference',
};

export default class ProjectListMatrixTable extends PureComponent {

  constructor(props) {
    super(props);

    this.statusCell = this.statusCell.bind(this);
    this.statusCellRenderer = this.statusCellRenderer.bind(this);
    this.statusTextCell = this.statusTextCell.bind(this);
    this.identifierCell = this.identifierCell.bind(this);
    this.assignedToCell = this.assignedToCell.bind(this);
    this.dueDateCell = this.dueDateCell.bind(this);
    this.documentCell = this.documentCell.bind(this);
    this.innerCell = this.innerCell.bind(this);
    this.getCellData = this.getCellData.bind(this);
    this.referencesCell = this.referencesCell.bind(this);
    this.handleRowSelect = this.handleRowSelect.bind(this);
    this.onColumnSort = this.onColumnSort.bind(this);
    this.onColumnToggle = this.onColumnToggle.bind(this);
    this.columns = [
      ISSUE_COLUMN_TYPES.STATUS,
      ISSUE_COLUMN_TYPES.ID,
      ISSUE_COLUMN_TYPES.TITLE,
      ISSUE_COLUMN_TYPES.ASSIGNED_TO,
      ISSUE_COLUMN_TYPES.COMPANY,
      ISSUE_COLUMN_TYPES.DUE_DATE,
      ISSUE_COLUMN_TYPES.LOCATION,
      ISSUE_COLUMN_TYPES.DOCUMENT,
      ISSUE_COLUMN_TYPES.COST,
      ISSUE_COLUMN_TYPES.SCHEDULE,
      ISSUE_COLUMN_TYPES.LINKED_REFERENCES,
      ISSUE_COLUMN_TYPES.PRIORITY,
      ISSUE_COLUMN_TYPES.DISCIPLINE,
      ISSUE_COLUMN_TYPES.CATEGORY,
      ISSUE_COLUMN_TYPES.REFERENCE,
      ISSUE_COLUMN_TYPES.CREATED_BY,
      ISSUE_COLUMN_TYPES.ATTACHMENTS,
      ISSUE_COLUMN_TYPES.COMMENTS,
    ];
  }

  statusCell({ cellData }) {
    return (
      <StatusColorIndicator statusColor={statusColorMapping[cellData]} />
    );
  }

  statusCellRenderer({ cellData, container }) {
    return <StatusCell status={cellData} showText={true} container={container} />;
  }

  statusTextCell({ cellData }) {
    return T.translate(`status.${cellData}`);
  }

  getStatusToolTipClass(statusKey) {
    return `ProjectListTable__status-column-tooltip IssueStatus__${statusKey}-tooltip`;
  }

  identifierCell({ cellData }) {
    if (!cellData) {
      return '-';
    }
    return (
      <Text ellipsis>{cellData}</Text>
    );
  }

  dueDateCell({ rowData }) {
    const isClosed = !!rowData.closedAt;
    const { dueDate } = rowData;
    return (
      <Text className={!isClosed && isLate(dueDate) ? 'ProjectListTable__due-date-column--is-late' : null} ellipsis>
        {dueDate ? toUtcLongDate(dueDate) : '-'}
      </Text>
    );
  }

  getCellData({ rowData, column }) {
    const cellData = rowData[column.dataKey];
    if (!cellData || !cellData.length) {
      return '-';
    }
    return Array.isArray(cellData) ? cellData.join(', ') : cellData;
  }

  handleRowSelect = ({ event, rowData }) => {
    // ctrl+click or cmd+click should have default behavior of anchor link
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    event.preventDefault();
    this.props.onRowClick({ attributes: rowData });
  };

  assignedToCell({ rowData }) {
    let assignedTo = findAssigneeInOxygenMappingById(rowData.assignedTo, this.props.oxygen) || rowData.assignedTo;
    if (!isSingleUserAssignee(rowData.assignees)) {
      assignedTo = this.props.oxygen[rowData.currentAssignee];
    }
    if (!assignedTo) {
      return '-';
    }

    return (
      <AssigneeCell
        primaryAssignee={assignedTo}
        currentUser={this.props.currentUser}
      />
    );
  }

  documentCell({ rowData }) {
    const { targetUrn, syncTooltipAnchoring } = rowData;
    if (!targetUrn) {
      return '-';
    }

    const isLoading = this.props.fetchingIssuesUrns && this.props.fetchingIssuesUrns.includes(targetUrn);

    return (
      <DocumentCell
        documentName={getDocumentName(this.props.documentCollection, targetUrn)}
        unavailableDocument={!isLoading && isDocumentUnavailable(this.props.documentCollection, targetUrn)}
        loadingDocument={isLoading}
        onDocumentClick={(e) => this.handleDocumentClick(e, rowData)}
        getDocumentNameRef={(anchoringRef) => {
          if (syncTooltipAnchoring) {
            syncTooltipAnchoring(anchoringRef);
          }
        }}
      />
    );
  }

  referencesCell({ rowData, container }) {
    return <ConnectedLinkedEntitiesCell
      container={container}
      plugins={[pco, meeting, issue]}
      sourceEntityId={rowData.id}
      sourceEntityType={ENTITY_TYPES.RFI}
      linkedEntitiesKey={rowData.linkedEntitiesKey}
    />;
  }

  handleDocumentClick(event, issue) {
    event.preventDefault();
    event.stopPropagation();
    const { targetUrn, id } = issue;
    this.props.navigateToIssueInDocumentAction(targetUrn, id, this.props.location);
  }

  innerCell({ cellData }) {
    return <div className="ProjectListTable__inner-cell">{cellData}</div>;
  }

  onColumnSort = ({ column }) => {
    this.props.onSort(column.sortField);
  };

  onColumnToggle({ column, hidden }) {
    const columnName = column.title;
    setTimeout(() => { this.props.trackEvent(hidden ? hideFieldColumnInTable(columnName) : showFieldColumnInTable(columnName)); });
    return true;
  }


  renderColumn(columnKey) {
    const key = ISSUE_COLUMN_TYPES[columnKey];
    switch (columnKey) {
      case ISSUE_COLUMN_TYPES.STATUS:
        return (
          <MatrixTable.Column
            key={key}
            dataKey="status"
            className="ProjectListTable__status-column"
            headerClassName="ProjectListTable-column-header-status"
            cellRenderer={this.statusCellRenderer}
            tooltipPosition={TooltipPositionings.RIGHT}
            tooltipData={this.statusTextCell}
            alwaysRenderTooltip
            getTooltipClass={this.getStatusToolTipClass}
            width={145}
            minWidth={145}
            sortable
            resizable
            sortField="status"
            title={T.translate('issueAttributes.status.label')}
            toggleable={false}
          />
        );
      case ISSUE_COLUMN_TYPES.ID:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.identifier.label')}
            dataKey={'customIdentifier'}
            className="ProjectListTable__identifier-column"
            sortField={'custom_identifier'}
            cellRenderer={this.identifierCell}
            minWidth={60}
            width={80}
            sortable
            resizable
            toggleable={false}
          />
        );
      case ISSUE_COLUMN_TYPES.TITLE:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.title.label')}
            dataKey="title"
            className="ProjectListTable__title-column"
            sortable
            resizable
            sortField="title"
            minWidth={150}
            width={355}
          />
        );
      case ISSUE_COLUMN_TYPES.LOCATION:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.location.label')}
            dataKey="locationDescription"
            className="ProjectListTable__location-column"
            sortable
            resizable
            sortField="location_description"
            minWidth={150}
            width={200}
          />
        );
      case ISSUE_COLUMN_TYPES.CREATED_BY:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.createdBy.label')}
            dataKey="createdByDisplayNameWithCompanyName"
            className="ProjectListTable__created-by-column"
            minWidth={150}
            width={200}
            resizable
          />
        );
      case ISSUE_COLUMN_TYPES.ASSIGNED_TO:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.assignedTo.label')}
            dataKey="assignedToDisplayName"
            className="ProjectListTable__assigned-to-column"
            cellRenderer={this.assignedToCell}
            minWidth={100}
            width={150}
            resizable
          />
        );
      case ISSUE_COLUMN_TYPES.COMPANY:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.company.label')}
            dataKey="companyName"
            className="ProjectListTable__company-column"
            minWidth={100}
            width={150}
            resizable
          />
        );
      case ISSUE_COLUMN_TYPES.DUE_DATE:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.dueDate.label')}
            dataKey="dueDate"
            className="ProjectListTable__due-date-column"
            sortable
            sortField="due_date"
            cellRenderer={this.dueDateCell}
            width={210}
            minWidth={100}
            resizable
          />
        );
      case ISSUE_COLUMN_TYPES.DOCUMENT:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.document.label')}
            dataKey="targetUrn"
            className="ProjectListTable__document-column"
            cellRenderer={this.documentCell}
            width={210}
            minWidth={100}
            resizable
            fetchingIssuesUrns={this.props.fetchingIssuesUrns} // Little trick to force re-rendering of column
          />
        );
      case ISSUE_COLUMN_TYPES.ATTACHMENTS:
        return (
          <MatrixTable.Column
            key={key}
            svgId="attachments-icon"
            headerRenderer={IconCell}
            cellRenderer={this.innerCell}
            dataKey="attachmentCount"
            className="ProjectListTable__attachments-column"
            title={T.translate('issueAttributes.attachmentCount.label')}
            width={40}
            minWidth={60}
            resizable
            align={Alignment.CENTER}
          />
        );
      case ISSUE_COLUMN_TYPES.COMMENTS:
        return (
          <MatrixTable.Column
            key={key}
            svgId="comments-icon"
            headerRenderer={IconCell}
            cellRenderer={this.innerCell}
            dataKey="commentCount"
            className="ProjectListTable__comments-column"
            title={T.translate('issueAttributes.commentCount.label')}
            width={40}
            minWidth={60}
            align={Alignment.CENTER}
            resizable
          />
        );
      case ISSUE_COLUMN_TYPES.COST:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.costImpact.label')}
            dataKey="costImpact"
            className="ProjectListTable__cost-column"
            sortable
            sortField="cost_impact"
            dataGetter={this.getCellData}
            width={110}
            minWidth={100}
            resizable
          />);
      case ISSUE_COLUMN_TYPES.SCHEDULE:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.scheduleImpact.label')}
            dataKey="scheduleImpact"
            className="ProjectListTable__schedule-column"
            dataGetter={this.getCellData}
            sortable
            sortField="schedule_impact"
            width={140}
            minWidth={140}
            resizable
          />);
      case ISSUE_COLUMN_TYPES.PRIORITY:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.priority.label')}
            dataKey="priority"
            className="ProjectListTable__priority-column"
            dataGetter={this.getCellData}
            sortable
            sortField="priority"
            width={110}
            minWidth={80}
            resizable
          />);
      case ISSUE_COLUMN_TYPES.DISCIPLINE:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.discipline.label')}
            dataKey="discipline"
            className="ProjectListTable__discipline-column"
            dataGetter={this.getCellData}
            sortable
            sortField="discipline"
            width={110}
            minWidth={80}
            resizable
          />);
      case ISSUE_COLUMN_TYPES.CATEGORY:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.category.label')}
            dataKey="category"
            className="ProjectListTable__category-column"
            dataGetter={this.getCellData}
            sortable
            sortField="category"
            width={110}
            minWidth={80}
            resizable
          />);

      case ISSUE_COLUMN_TYPES.REFERENCE:
        return (
          <MatrixTable.Column
            key={key}
            title={T.translate('issueAttributes.reference.label')}
            dataKey="reference"
            className="ProjectListTable__reference-column"
            dataGetter={this.getCellData}
            sortable
            sortField="reference"
            width={110}
            minWidth={80}
            resizable
          />);
      case ISSUE_COLUMN_TYPES.LINKED_REFERENCES:
        const shouldRenderReferencesColumn = featureFlags.get(FEATURE_FLAGS.RFIS_REFERENCED_BY) || featureFlags.get(FEATURE_FLAGS.RFIS_LINK_TO_PCO);

        if (!shouldRenderReferencesColumn) {
          return null;
        }

        return (
          <MatrixTable.Column
            key={key}
            cellRenderer={this.referencesCell}
            dataGetter={this.getCellData}
            dataKey="linked-items"
            width={150}
            minWidth={150}
            title={T.translate('issueAttributes.linkedReferences.label')}
            resizable
          />);
      default:
        return null;
    }
  }

  renderColumns() {
    const columns = [];
    this.columns.forEach((column) => {
      const renderedColumn = this.renderColumn(column);
      renderedColumn && columns.push(renderedColumn);
    });
    return columns;
  }

  render() {
    const { tableData, currentSort, currentUser, projectId } = this.props;
    const storageKey = `rfis__${projectId}__${currentUser.analyticsId}`;
    const data = tableData.map(row => row.attributes);
    const sort = {};
    if (currentSort) {
      const sortColumnKey = Object.keys(ProjectListMatrixTable.columnSortField).find(key => ProjectListMatrixTable.columnSortField[key] === currentSort.sortField);
      if (sortColumnKey) {
        sort.key = sortColumnKey;
        sort.order = currentSort.isAscending ? MatrixTable.SortOrder.ASC : MatrixTable.SortOrder.DESC;
      }
    }

    return (
      <div>
        <MatrixTable
          attachTestId
          data={data}
          storageKey={storageKey}
          autoGrow={data.length > 0}
          className="ProjectListTable"
          rowClassName="AccountTemplateCollectionTable__row"
          sortBy={sort}
          onRowClick={this.handleRowSelect}
          onColumnSort={this.onColumnSort}
          onColumnHide={this.onColumnToggle}
          showSettings
          fixed={[]}
        >
          {this.renderColumns()}
        </MatrixTable>
      </div>
    );
  }
}

ProjectListMatrixTable.columnSortField = {
  [ISSUE_COLUMN_TYPES.STATUS]: SORTABLE_COLUMN_NAMES.STATUS,
  [ISSUE_COLUMN_TYPES.ID]: SORTABLE_COLUMN_NAMES.ID,
  [ISSUE_COLUMN_TYPES.TITLE]: SORTABLE_COLUMN_NAMES.TITLE,
  [ISSUE_COLUMN_TYPES.LOCATION]: SORTABLE_COLUMN_NAMES.LOCATION,
  [ISSUE_COLUMN_TYPES.DUE_DATE]: SORTABLE_COLUMN_NAMES.DUE_DATE,
  [ISSUE_COLUMN_TYPES.SCHEDULE]: SORTABLE_COLUMN_NAMES.SCHEDULE,
  [ISSUE_COLUMN_TYPES.COST]: SORTABLE_COLUMN_NAMES.COST,
  [ISSUE_COLUMN_TYPES.PRIORITY]: SORTABLE_COLUMN_NAMES.PRIORITY,
  [ISSUE_COLUMN_TYPES.CATEGORY]: SORTABLE_COLUMN_NAMES.CATEGORY,
  [ISSUE_COLUMN_TYPES.DISCIPLINE]: SORTABLE_COLUMN_NAMES.DISCIPLINE,
  [ISSUE_COLUMN_TYPES.REFERENCE]: SORTABLE_COLUMN_NAMES.REFERENCE,
};

ProjectListMatrixTable.displayName = 'ProjectListTable';

ProjectListMatrixTable.propTypes = {
  tableData: issueWrapperArrayType.isRequired,
  currentSort: sortPropType.isRequired,
  onSort: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  documentCollection: documentCollectionType,
  navigateToIssueInDocumentAction: PropTypes.func.isRequired,
  location: PropTypes.object,
  fetchingIssuesUrns: PropTypes.array,
  rowLinkEnabled: PropTypes.bool.isRequired,
  currentUser: userType,
  oxygen: oxygenType.isRequired,
  trackEvent: PropTypes.func.isRequired,
  projectId: PropTypes.string,
};
